import React, { useState } from 'react';
import PropTypes from 'prop-types';

const SoftwareBoxMockup = ({ product, lang = 'en', onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    const productName = lang === 'ka' 
        ? (product.name_ka || product.name_en) 
        : (product.name_en || product.name_ka);

    const solidColor = product.badge_color || '#4f46e5';

    // Sector resolution
    const getSectorLabel = () => {
        const market = product.target_market || product.sector || 'municipal';
        if (lang === 'ka') {
            switch (market) {
                case 'municipal': return 'მუნიციპალური სექტორი';
                case 'government': return 'სამთავრობო სექტორი';
                case 'business': return 'ბიზნეს სექტორი';
                case 'healthcare': return 'ჯანდაცვის სექტორი';
                case 'education': return 'განათლების სექტორი';
                case 'banking': return 'საფინანსო სექტორი';
                default: return 'კორპორატიული სექტორი';
            }
        }
        switch (market) {
            case 'municipal': return 'Municipal Sector';
            case 'government': return 'Government Sector';
            case 'business': return 'Business Enterprise';
            case 'healthcare': return 'Healthcare Sector';
            case 'education': return 'Education Sector';
            case 'banking': return 'Financial Sector';
            default: return 'Enterprise Sector';
        }
    };

    const sectorLabel = getSectorLabel();

    return (
        <div 
            className="sw-box-viewport"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
        >
            <div 
                className={`sw-3d-box ${isHovered ? 'hovered' : ''}`}
                style={{ '--box-solid': solidColor }}
            >
                {/* ── Ground Shadows ── */}
                <div className="sw-floor-shadow" />
                <div className="sw-ambient-shadow" />

                {/* ── 1. Front Face (Primary Solid Color Face) ── */}
                <div className="sw-face sw-face-front">
                    {/* Sector Tag at Top */}
                    <div className="sw-sector-wrap">
                        <span className="sw-sector-pill">
                            <span className="sw-sector-dot" />
                            {sectorLabel}
                        </span>
                        <span className="sw-brand-pill">ORBX</span>
                    </div>

                    {/* Central Icon */}
                    <div className="sw-icon-wrap">
                        <div className="sw-icon-frame">
                            {product.thumbnail ? (
                                <img 
                                    src={product.thumbnail} 
                                    alt={productName} 
                                    title={productName}
                                    className="sw-icon-img"
                                />
                            ) : (
                                <span className="sw-icon-emoji">{product.icon || '📦'}</span>
                            )}
                        </div>
                    </div>

                    {/* Product Name at Bottom */}
                    <div className="sw-meta-bottom">
                        <h4 className="sw-product-title" title={productName}>
                            {productName}
                        </h4>
                        <div className="sw-edition-tag">
                            ENTERPRISE SOFTWARE SUITE
                        </div>
                    </div>

                    {/* Specular Surface Sheen & Animated Light Sweep on Hover */}
                    <div className="sw-specular-glare" />
                    <div className="sw-sheen-sweep" />
                    <div className="sw-box-bevel" />
                </div>

                {/* ── 2. Left Spine Face (Connected Seamlessly) ── */}
                <div className="sw-face sw-face-spine">
                    <div className="sw-spine-inner">
                        <div className="sw-spine-head">
                            <span className="sw-spine-logo">ORBX</span>
                        </div>
                        <div className="sw-spine-title-box">
                            <span className="sw-spine-title-text">{productName}</span>
                        </div>
                        <div className="sw-spine-foot">
                            <span className="sw-spine-indicator" />
                        </div>
                    </div>
                </div>

                {/* ── 3. Top Face (Lid) ── */}
                <div className="sw-face sw-face-top">
                    <div className="sw-top-inner">
                        <span className="sw-top-brand">ORBITRONIX</span>
                    </div>
                </div>

                {/* ── 4. Right Face ── */}
                <div className="sw-face sw-face-right" />

                {/* ── 5. Bottom Face ── */}
                <div className="sw-face sw-face-bottom" />

                {/* ── 6. Back Face ── */}
                <div className="sw-face sw-face-back" />
            </div>

            <style>{`
                .sw-box-viewport {
                    perspective: 1100px;
                    perspective-origin: 50% 38%;
                    width: 220px;
                    height: 310px;
                    margin: 0 auto;
                    position: relative;
                    cursor: pointer;
                    user-select: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* ── 3D Box Container (Ideal Standing Position) ── */
                .sw-3d-box {
                    width: 180px;
                    height: 260px;
                    position: relative;
                    transform-style: preserve-3d;
                    /* Ideal, perfectly grounded and plumb resting posture */
                    transform: rotateY(-18deg) rotateX(1.5deg);
                    transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                }

                /* ── Dynamic 3D Showcase Animation on Hover ── */
                .sw-box-viewport:hover .sw-3d-box,
                .hp-card:hover .sw-3d-box,
                .pd-card-item:hover .sw-3d-box,
                .sw-3d-box.hovered {
                    animation: swBoxShowcase 4s ease-in-out infinite alternate;
                }

                @keyframes swBoxShowcase {
                    0% {
                        transform: translateY(-12px) rotateY(-12deg) rotateX(2deg) scale(1.03);
                    }
                    50% {
                        transform: translateY(-22px) rotateY(-24deg) rotateX(5deg) scale(1.05);
                    }
                    100% {
                        transform: translateY(-15px) rotateY(-8deg) rotateX(2deg) scale(1.04);
                    }
                }

                /* ── Ground Shadows in Ideal Position ── */
                .sw-floor-shadow {
                    position: absolute;
                    width: 195px;
                    height: 42px;
                    bottom: -22px;
                    left: -8px;
                    background: radial-gradient(ellipse at 46% 50%, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.3) 50%, transparent 75%);
                    transform: rotateX(86deg) rotateY(-8deg);
                    filter: blur(6px);
                    border-radius: 50%;
                    pointer-events: none;
                    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
                }

                .sw-ambient-shadow {
                    position: absolute;
                    width: 160px;
                    height: 12px;
                    bottom: -6px;
                    left: 10px;
                    background: rgba(0, 0, 0, 0.88);
                    filter: blur(3px);
                    border-radius: 50%;
                    transform: rotateX(86deg);
                    pointer-events: none;
                    transition: all 0.5s ease;
                }

                /* Shadow Animation on Hover */
                .sw-box-viewport:hover .sw-floor-shadow,
                .hp-card:hover .sw-floor-shadow,
                .pd-card-item:hover .sw-floor-shadow,
                .sw-3d-box.hovered .sw-floor-shadow {
                    animation: swShadowFloat 4s ease-in-out infinite alternate;
                }

                @keyframes swShadowFloat {
                    0% {
                        transform: rotateX(86deg) rotateY(-8deg) scale(1.05);
                        filter: blur(9px);
                        opacity: 0.65;
                    }
                    50% {
                        transform: rotateX(86deg) rotateY(-8deg) scale(1.22);
                        filter: blur(14px);
                        opacity: 0.4;
                    }
                    100% {
                        transform: rotateX(86deg) rotateY(-8deg) scale(1.08);
                        filter: blur(10px);
                        opacity: 0.6;
                    }
                }

                .sw-box-viewport:hover .sw-ambient-shadow,
                .hp-card:hover .sw-ambient-shadow,
                .pd-card-item:hover .sw-ambient-shadow,
                .sw-3d-box.hovered .sw-ambient-shadow {
                    opacity: 0.3;
                    filter: blur(5px);
                }

                /* ── Common Face Styling ── */
                .sw-face {
                    position: absolute;
                    box-sizing: border-box;
                    background-color: var(--box-solid, #4f46e5);
                    backface-visibility: hidden;
                }

                /* ── 1. Front Face (180px x 260px, translateZ = +18px) ── */
                .sw-face-front {
                    width: 180px;
                    height: 260px;
                    top: 0;
                    left: 0;
                    transform: translateZ(18px);
                    border-radius: 4px;
                    padding: 14px 12px 14px;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    overflow: hidden;
                    border: 1px solid rgba(255, 255, 255, 0.25);
                    box-shadow: inset 1px 1px 0 rgba(255, 255, 255, 0.35);
                    z-index: 3;
                }

                /* Sector Pill Row */
                .sw-sector-wrap {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 6px;
                    position: relative;
                    z-index: 2;
                }
                .sw-sector-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 8px;
                    font-weight: 700;
                    letter-spacing: 0.6px;
                    text-transform: uppercase;
                    color: #ffffff;
                    background: rgba(0, 0, 0, 0.22);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    padding: 3px 7px;
                    border-radius: 12px;
                    max-width: 132px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .sw-sector-dot {
                    width: 5px;
                    height: 5px;
                    border-radius: 50%;
                    background: #ffffff;
                    box-shadow: 0 0 5px rgba(255, 255, 255, 0.8);
                    flex-shrink: 0;
                }
                .sw-brand-pill {
                    font-size: 8px;
                    font-weight: 900;
                    letter-spacing: 1px;
                    color: rgba(255, 255, 255, 0.9);
                    background: rgba(255, 255, 255, 0.18);
                    padding: 2px 5px;
                    border-radius: 4px;
                }

                /* Central Icon Frame */
                .sw-icon-wrap {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 10px 0;
                    position: relative;
                    z-index: 2;
                }
                .sw-icon-frame {
                    width: 80px;
                    height: 80px;
                    border-radius: 20px;
                    background: rgba(255, 255, 255, 0.2);
                    border: 1.5px solid rgba(255, 255, 255, 0.45);
                    box-shadow: 
                        0 12px 28px rgba(0, 0, 0, 0.25),
                        inset 0 1px 1px rgba(255, 255, 255, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.4s ease;
                }
                .sw-box-viewport:hover .sw-icon-frame,
                .hp-card:hover .sw-icon-frame,
                .pd-card-item:hover .sw-icon-frame,
                .sw-3d-box.hovered .sw-icon-frame {
                    transform: scale(1.08);
                }
                .sw-icon-emoji {
                    font-size: 38px;
                    line-height: 1;
                    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.35));
                }
                .sw-icon-img {
                    width: 50px;
                    height: 50px;
                    object-fit: contain;
                    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.35));
                }

                /* Meta Bottom */
                .sw-meta-bottom {
                    position: relative;
                    z-index: 2;
                    text-align: left;
                }
                .sw-product-title {
                    font-size: 14px;
                    font-weight: 800;
                    line-height: 1.25;
                    color: #ffffff;
                    margin: 0 0 5px 0;
                    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    letter-spacing: -0.2px;
                }
                .sw-edition-tag {
                    font-size: 7.5px;
                    font-weight: 800;
                    letter-spacing: 1.2px;
                    color: rgba(255, 255, 255, 0.85);
                    text-transform: uppercase;
                }

                /* Glare & Sheen Sweep on Hover */
                .sw-specular-glare {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        130deg,
                        rgba(255, 255, 255, 0.28) 0%,
                        rgba(255, 255, 255, 0.08) 32%,
                        transparent 60%
                    );
                    pointer-events: none;
                    z-index: 5;
                }

                .sw-sheen-sweep {
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: linear-gradient(
                        105deg,
                        transparent 35%,
                        rgba(255, 255, 255, 0.35) 45%,
                        rgba(255, 255, 255, 0.55) 50%,
                        rgba(255, 255, 255, 0.35) 55%,
                        transparent 65%
                    );
                    transform: translateX(-150%) rotate(25deg);
                    pointer-events: none;
                    z-index: 6;
                    opacity: 0;
                }

                .sw-box-viewport:hover .sw-sheen-sweep,
                .hp-card:hover .sw-sheen-sweep,
                .pd-card-item:hover .sw-sheen-sweep,
                .sw-3d-box.hovered .sw-sheen-sweep {
                    animation: swSheenPass 2s cubic-bezier(0.2, 0.8, 0.2, 1) infinite;
                }

                @keyframes swSheenPass {
                    0% {
                        transform: translateX(-150%) rotate(25deg);
                        opacity: 0;
                    }
                    15% {
                        opacity: 1;
                    }
                    85% {
                        opacity: 1;
                    }
                    100% {
                        transform: translateX(150%) rotate(25deg);
                        opacity: 0;
                    }
                }

                .sw-box-bevel {
                    position: absolute;
                    inset: 0;
                    box-shadow: inset -1px -1px 2px rgba(0, 0, 0, 0.4);
                    pointer-events: none;
                    z-index: 7;
                }

                /* ── 2. Left Spine (36px x 260px, centered on X=0, rotateY(-90deg)) ── */
                .sw-face-spine {
                    width: 36px;
                    height: 260px;
                    top: 0;
                    left: -18px;
                    transform: rotateY(-90deg);
                    filter: brightness(0.72);
                    border-top: 1px solid rgba(255, 255, 255, 0.3);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.8);
                    box-shadow: inset -3px 0 8px rgba(0, 0, 0, 0.4);
                    z-index: 2;
                }
                .sw-spine-inner {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 0 10px;
                    color: #ffffff;
                }
                .sw-spine-logo {
                    font-size: 8px;
                    font-weight: 900;
                    letter-spacing: 1.4px;
                    color: #ffffff;
                }
                .sw-spine-title-box {
                    writing-mode: vertical-rl;
                    transform: rotate(180deg);
                    display: flex;
                    align-items: center;
                    max-height: 165px;
                    overflow: hidden;
                }
                .sw-spine-title-text {
                    font-size: 9.5px;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                    color: #ffffff;
                    white-space: nowrap;
                    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
                }
                .sw-spine-indicator {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #ffffff;
                    box-shadow: 0 0 6px rgba(255, 255, 255, 0.9);
                }

                /* ── 3. Top Face (180px x 36px, centered on Y=0, rotateX(90deg)) ── */
                .sw-face-top {
                    width: 180px;
                    height: 36px;
                    top: -18px;
                    left: 0;
                    transform: rotateX(90deg);
                    filter: brightness(1.22);
                    border-left: 1px solid rgba(255, 255, 255, 0.4);
                    border-right: 1px solid rgba(0, 0, 0, 0.4);
                    z-index: 2;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .sw-top-inner {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                }
                .sw-top-brand {
                    font-size: 7.5px;
                    font-weight: 900;
                    letter-spacing: 2px;
                    color: rgba(255, 255, 255, 0.75);
                }

                /* ── 4. Right Face (36px x 260px, centered on X=180, rotateY(90deg)) ── */
                .sw-face-right {
                    width: 36px;
                    height: 260px;
                    top: 0;
                    right: -18px;
                    transform: rotateY(90deg);
                    filter: brightness(0.65);
                }

                /* ── 5. Bottom Face (180px x 36px, centered on Y=260, rotateX(-90deg)) ── */
                .sw-face-bottom {
                    width: 180px;
                    height: 36px;
                    bottom: -18px;
                    left: 0;
                    transform: rotateX(-90deg);
                    filter: brightness(0.45);
                }

                /* ── 6. Back Face (180px x 260px, translateZ = -18px) ── */
                .sw-face-back {
                    width: 180px;
                    height: 260px;
                    top: 0;
                    left: 0;
                    transform: rotateY(180deg) translateZ(18px);
                    filter: brightness(0.78);
                }

                @media (max-width: 576px) {
                    .sw-box-viewport {
                        width: 190px;
                        height: 280px;
                    }
                    .sw-3d-box {
                        transform: scale(0.9) rotateY(-18deg) rotateX(1.5deg);
                    }
                }
            `}</style>
        </div>
    );
};

SoftwareBoxMockup.propTypes = {
    product: PropTypes.object.isRequired,
    lang: PropTypes.string,
    onClick: PropTypes.func
};

export default SoftwareBoxMockup;
